import { omitBy, isUndefined } from 'lodash';

import {
  DealerReviewInterface,
  DealerReviewApiResponse,
  DealerReviewsInterface,
  DealerReviewsApiResponse,
  ModelReviewsApiResponse,
  ModelReviewsInterface,
  ModelReviewApiResponse,
  ModelReviewInterface,
} from 'types/Apis/SearchURL';
import { MakeResponse } from 'types/MakeResponse';
import { ResponseRecord } from 'types/Apis/SearchResponse';
import { ListingViewModel } from 'components/Shared/ListingsCarousel/types';
import {
  VehicleV2,
  VehicleV2ApiResponseData,
  TextSnippetApiResponse,
  TextSnippetsApiResponse,
  VehicleOverviewsInterface,
  VehicleOverviewInterface,
  ArticlesApiResponse,
  ArticlePreview,
  DrivingExperiencesApiResponse,
  DrivingExperiences,
  SearchV2AdInfoResponseData,
  SearchV2AdInfo,
} from './types';

export function transformAdInfo(apiData: SearchV2AdInfoResponseData): SearchV2AdInfo {
  return {
    adUnit: apiData.ad_unit,
    make: apiData.make,
    model: apiData.model,
    year: apiData.year,
    pageType: apiData.page_type,
    ageType: apiData.age_type,
    cpo: apiData.cpo,
    inLine: apiData.in_line,
    // intentionally leaving this as snake case
    // https://autolist.slack.com/archives/CHA7W6PGF/p1701462625670549?thread_ts=1701452109.414969&cid=CHA7W6PGF
    body_style: apiData.body_style,
    category: apiData.category,
    app: apiData.app,
    sellerId: apiData.seller_id,
  };
}

export function transformSrpVehicle(apiData: VehicleV2ApiResponseData): VehicleV2 {
  // eslint-disable-next-line camelcase
  const { city, state, created_at } = apiData;
  const createdAt = Date.parse(created_at);
  const currentDate = Date.parse(new Date().toDateString());
  return {
    id: apiData.id,
    vin: apiData.vin,
    hasImages: apiData.has_photos,
    images: apiData.photo_urls,
    make: apiData.make_name,
    model: apiData.model_name,
    year: apiData.year,
    city,
    state,
    justListed: currentDate - createdAt < 86400000,
    mileage: apiData.mileage,
    showNewMileage: apiData.show_new_mileage,
    price: apiData.price,
    location: `${city}, ${state}`,
    dealerName: apiData.dealer_name,
    isHot: apiData.is_hot,
    hasRecentPriceDrop: apiData.recent_price_drop,
    trackingParams: apiData.tracking_params,
    isRegional: apiData.regional,
    availableNationwide: apiData.available_nationwide,
    carmax: apiData.provider_name === 'carmax',
    partnerType: apiData.partner_type,
    cplValue: apiData.cpl_value,
    zip: apiData.zip || '',
    buyerIntelligence: {
      relativePriceDiff: apiData.relative_price_difference,
      totalPriceChange: apiData.total_price_change,
      timeOnMarket: apiData.time_on_market,
    },
    isQuickPicksEligible: apiData.quick_picks_eligible,
    trim: apiData.trim,
    vdpUrl: apiData.vdp_url || `${apiData.make_name}-${apiData.model_name}#vin=${apiData.vin}`,
    bodyType: apiData.body_type,
    doorCount: apiData.door_count,
    driveline: apiData.driveline,
    fuelType: apiData.fuel_type,
    transmission: apiData.transmission,
    combinedMpg: apiData.combined_mpg,
    normalizedColorExterior: apiData.normalized_color_exterior,
    normalizedColorInterior: apiData.normalized_color_interior,
  };
}

export function transformTextSnippetApiResponse(
  textSnippet: TextSnippetApiResponse
): VehicleOverviewInterface {
  return {
    title: textSnippet.title,
    body: textSnippet.body,
  };
}

export function transformTextSnippetsApiResponse(
  textSnippets: TextSnippetsApiResponse | undefined
): VehicleOverviewsInterface {
  if (!textSnippets) {
    return {
      vehicleOverviews: [],
    };
  }

  return {
    vehicleOverviews: textSnippets.text_snippets.map(transformTextSnippetApiResponse),
  };
}

function transformDealerReview(review: DealerReviewApiResponse): DealerReviewInterface {
  return {
    id: review.id,
    writtenAt: review.written_at,
    modelId: review.model_id,
    makeName: review.make_name,
    modelName: review.model_name,
    year: review.year,
    modelYear: review.model_year,
    dealerName: review.dealer_name,
    bestLocationDisplayName: review.best_location_display_name,
    reviewTitle: review.review_title,
    review: review.review,
    extraReview: review.extra_review,
    reviewerName: review.reviewer_name,
    overallRating: review.overall_rating,
    facilityRating: review.facility_rating,
    processRating: review.process_rating,
    qualityRating: review.quality_rating,
    serviceRating: review.service_rating,
    priceRating: review.price_rating,
    helpRating: review.help_rating,
  };
}

export function transformDealerReviewsApiResponse(
  dealerReviewsApiResponse: DealerReviewsApiResponse
): DealerReviewsInterface {
  const transformed = {
    dealerReviews: dealerReviewsApiResponse.dealer_reviews.map(transformDealerReview),
    pagination: {
      currentPage: dealerReviewsApiResponse.pagination.current_page,
      totalPages: dealerReviewsApiResponse.pagination.total_pages,
    },
    dealerReviewSummary: {
      reviewCount: dealerReviewsApiResponse.dealer_review_summary.review_count,
      overallRating: dealerReviewsApiResponse.dealer_review_summary.overall_rating,
      recommendationCount: dealerReviewsApiResponse.dealer_review_summary.recommendation_count,
      countsByRating: {
        oneStar: dealerReviewsApiResponse.dealer_review_summary.counts_by_rating['1_star'],
        twoStar: dealerReviewsApiResponse.dealer_review_summary.counts_by_rating['2_star'],
        threeStar: dealerReviewsApiResponse.dealer_review_summary.counts_by_rating['3_star'],
        fourStar: dealerReviewsApiResponse.dealer_review_summary.counts_by_rating['4_star'],
        fiveStar: dealerReviewsApiResponse.dealer_review_summary.counts_by_rating['5_star'],
      },
      averagesByCategory: {
        pricingRating:
          dealerReviewsApiResponse.dealer_review_summary.averages_by_category.pricing_rating,
        serviceRating:
          dealerReviewsApiResponse.dealer_review_summary.averages_by_category.service_rating,
        helpfulnessRating:
          dealerReviewsApiResponse.dealer_review_summary.averages_by_category.helpfulness_rating,
        responsivenessRating:
          dealerReviewsApiResponse.dealer_review_summary.averages_by_category.responsiveness_rating,
      },
    },
  };

  return transformed;
}

export function transformModelReview(review: ModelReviewApiResponse): ModelReviewInterface {
  return {
    id: review.id,
    makeName: review.make_name || '',
    modelName: review.model_name || '',
    modelYear: review.model_year || 0,
    title: review.title,
    body: review.body,
    pros: review.pros,
    cons: review.cons,
    story: review.story,
    bestLocationDisplayName: review.best_location_display_name,
    reviewerName: review.reviewer_name,
    overallRating: review.overall_rating,
    valueRating: review.value_rating,
    performanceRating: review.performance_rating,
    styleRating: review.style_rating,
    comfortRating: review.comfort_rating,
    fuelEconomyRating: review.fuel_economy_rating,
    maintenanceRating: review.maintenance_rating,
  };
}

export function transformModelReviewsApiResponse(
  dealerReviews: ModelReviewsApiResponse
): ModelReviewsInterface {
  return {
    pagination: {
      currentPage: dealerReviews.pagination.current_page,
      totalPages: dealerReviews.pagination.total_pages,
    },
    ownerReviewSummary: {
      reviewCount: dealerReviews.model_review_summary.review_count,
      overallRating: dealerReviews.model_review_summary.overall_rating,
      breakdownByRatingPercentages: {
        fiveStar: dealerReviews.model_review_summary.counts_by_rating['5_star'] || 0,
        fourStar: dealerReviews.model_review_summary.counts_by_rating['4_star'] || 0,
        threeStar: dealerReviews.model_review_summary.counts_by_rating['3_star'] || 0,
        twoStar: dealerReviews.model_review_summary.counts_by_rating['2_star'] || 0,
        oneStar: dealerReviews.model_review_summary.counts_by_rating['1_star'] || 0,
      },
      breakdownByCategoryAverages: {
        valueRating: dealerReviews.model_review_summary.averages_by_category.value_rating,
        performanceRating:
          dealerReviews.model_review_summary.averages_by_category.performance_rating,
        styleRating: dealerReviews.model_review_summary.averages_by_category.style_rating,
        comfortRating: dealerReviews.model_review_summary.averages_by_category.comfort_rating,
        fuelEconomyRating:
          dealerReviews.model_review_summary.averages_by_category.fuel_economy_rating,
        maintenanceRating:
          dealerReviews.model_review_summary.averages_by_category.maintenance_rating,
      },
    },
    ownerReviews: dealerReviews.model_reviews.map((review) => transformModelReview(review)),
  };
}

export function transformMakesAndModels(makesAndModels: MakeResponse[]): MakeResponse[] {
  return makesAndModels.sort((a, b) => {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  });
}

export function transformArticlesApiResponse(
  vehicleArticlesApiResponse: ArticlesApiResponse
): ArticlePreview[] {
  return vehicleArticlesApiResponse.articles.map((vehicleArticleApiResponse) => {
    const articlePreview = omitBy<ArticlePreview>(
      {
        author: {
          name: vehicleArticleApiResponse.author.name,
        },
        parentPage: {
          name: vehicleArticleApiResponse.parent_page.name,
          slug: vehicleArticleApiResponse.parent_page.slug,
        },
        contentSummary: vehicleArticleApiResponse.content_summary,
        publicationDate: vehicleArticleApiResponse.publication_date,
        slug: vehicleArticleApiResponse.slug,
        thumbImage: vehicleArticleApiResponse.thumb_image,
        title: vehicleArticleApiResponse.title,
      },
      isUndefined
    );

    return articlePreview as ArticlePreview;
  });
}

export function transformDrivingExperiencesApiResponse(
  drivingExperiencesApiResponse: DrivingExperiencesApiResponse,
  cityId: number
): DrivingExperiences {
  return {
    carNeed: {
      totalVotes: drivingExperiencesApiResponse.car_need.total_votes,
      mustHave: drivingExperiencesApiResponse.car_need.must_have,
      niceToHave: drivingExperiencesApiResponse.car_need.nice_to_have,
      canGetByWithout: drivingExperiencesApiResponse.car_need.can_get_by_without,
      notReally: drivingExperiencesApiResponse.car_need.not_really,
      noNeed: drivingExperiencesApiResponse.car_need.no_need,
    },
    experiences: drivingExperiencesApiResponse.experiences.map((experience) => ({
      author: experience.author,
      city: experience.city,
      id: experience.id,
      carNeed: experience.car_need,
      favoriteMemory: experience.favorite_memory,
      favoriteRoad: experience.favorite_road,
      whyRoad: experience.why_road,
      drivingTip: experience.driving_tip,
      buyingTip: experience.buying_tip,
    })),
    pagination: {
      currentPage: drivingExperiencesApiResponse.pagination.current_page,
      totalPages: drivingExperiencesApiResponse.pagination.total_pages,
    },
    cityId,
  };
}

export function transformSimilarListingsApiResponse(
  similarListingsApiResponse: ResponseRecord[]
): ListingViewModel[] {
  return similarListingsApiResponse.map((responseRecord): ListingViewModel => {
    return {
      year: responseRecord.year,
      make: responseRecord.make,
      model: responseRecord.model,
      mileageUnformatted: responseRecord.mileage_unformatted,
      price: responseRecord.price,
      city: responseRecord.city,
      state: responseRecord.state,
      vdpUrl: responseRecord.vdp_url,
      primaryPhotoUrl: responseRecord.primary_photo_url,
      vin: responseRecord.vin,
    };
  });
}
